import {html, LitElement, TemplateResult} from "lit";
import {customElement, state} from "lit/decorators.js";
import {intersects} from "../utils/collision";
import gameStyles from "../../../../styles/minigames/dragoninvaders/gameStyles";
import {postScore} from "../../../../services/routeService";
import {difficultyEnum, difficultyIndex} from "../../../../utils/handeEnums";
import {getDifficultyFromSession} from "../../../../utils/gameUtils";

@customElement("game-board")
export class GameBoard extends LitElement {
    @state() private enemies: Array<{
        position: { x: number, y: number },
        size: { width: number, height: number },
        health: number,
        projectileSpeed: number,
        type: number,
        imageUrl: string,
        scoreValue: number
    }> = [];
    @state() private projectiles: Array<{
        positionX: number,
        positionY: number,
        direction: "up" | "down",
        shooter: "player" | "enemy",
        speed: number
    }> = [];
    @state() private player: {
        position: { x: number, y: number },
        size: { width: number, height: number },
        alive: boolean
    } = {
        position: {x: this.offsetWidth / 2, y: this.offsetHeight - 100},
        size: {width: 50, height: 20},
        alive: true
    };
    @state() private score: number = 0;
    @state() private enemyDirection: "left" | "right" = "right";
    @state() private enemyDropDown: boolean = false;
    @state() private moveDownStep: number = 20;
    @state() private enemySpeed: number = 1;
    private speedIncrement: number = .5;

    @state() private gameStarted: boolean = false;
    @state() private difficulty: number = 1;

    private enemyTypes = [
        {
            health: 1,
            scoreValue: 10,
            projectileSpeed: 1,
            imageUrl: "url(\"assets/img/minigames/dragoninvaders/dragons/pink-dragon.png\")"
        },
        {
            health: 1,
            scoreValue: 15,
            projectileSpeed: 2,
            imageUrl: "url(\"assets/img/minigames/dragoninvaders/dragons/blue-dragon.png\")"
        },
        {
            health: 2,
            scoreValue: 20,
            projectileSpeed: 1,
            imageUrl: "url(\"assets/img/minigames/dragoninvaders/dragons/green-dragon.png\")"
        },
        {
            health: 2,
            scoreValue: 25,
            projectileSpeed: 2,
            imageUrl: "url(\"assets/img/minigames/dragoninvaders/dragons/red-dragon.png\")"
        },
    ];

    public static styles = [gameStyles];

    public connectedCallback(): void {
        super.connectedCallback();

        // Listen for the custom event with a typed event parameter
        this.addEventListener("player-position-change", (event: Event) => {
            const customEvent: CustomEvent<{ positionX: number }> = event as CustomEvent<{
                positionX: number
            }>;
            const {positionX} = customEvent.detail;

            this.player.position.x = positionX / 100 * this.offsetWidth;
            this.requestUpdate();
        });

        this.addEventListener("shoot", (e: Event) => this.handleShoot(e as CustomEvent<{
            positionX: number,
            positionY: number
        }>));
    }

    private handleShoot(e: CustomEvent<{ positionX: number; positionY: number }>): void {
        const {positionX, positionY} = e.detail;

        this.projectiles.push({
            positionX: positionX,
            positionY: positionY,
            direction: "up",
            shooter: "player",
            speed: 5
        });

        this.requestUpdate();
    }

    private enemyShoot(): void {
        const shootingFeq: number = (0.01 * this.enemySpeed) * (this.difficulty / 2);


        // Randomly decide whether an enemy will shoot in this frame
        if (Math.random() < shootingFeq && this.enemies.length > 0) { // Adjust the 0.01 to control shooting frequency
            const shootingEnemyIndex: number = Math.floor(Math.random() * this.enemies.length);
            const shootingEnemy: {
                position: { x: number; y: number };
                size: { width: number; height: number }
                projectileSpeed: number
            } = this.enemies[shootingEnemyIndex];


            this.projectiles.push({
                positionX: shootingEnemy.position.x + shootingEnemy.size.width / 2, // Center the projectile
                positionY: shootingEnemy.position.y + shootingEnemy.size.height, // Start at the bottom of the enemy
                direction: "down",
                shooter: "enemy",
                speed: (shootingEnemy.projectileSpeed * this.enemySpeed) * (this.difficulty / 2)
            });
        }
    }

    private startGame(): void {
        this.spawnEnemyGroup();
        requestAnimationFrame(() => this.updateGame());
    }

    private spawnEnemyGroup(): void {
        const rows: number = 3;
        const columns: number = 5;
        const enemySpacing: number = 100;

        for (let row: number = 0; row < rows; row++) {
            for (let col: number = 0; col < columns; col++) {
                const typeIndex: number = Math.floor(Math.random() * this.enemyTypes.length);
                const enemyType: {
                    health: number,
                    scoreValue: number,
                    projectileSpeed: number,
                    imageUrl: string,
                } = this.enemyTypes[typeIndex];

                this.enemies.push({
                    position: {
                        x: col * enemySpacing + (this.offsetWidth - columns * enemySpacing) / 2,
                        y: row * enemySpacing,
                    },
                    size: {width: 150, height: 150},
                    health: enemyType.health,
                    projectileSpeed: enemyType.projectileSpeed,
                    type: typeIndex,
                    imageUrl: enemyType.imageUrl,
                    scoreValue: enemyType.scoreValue
                });
            }
        }
    }


    private updateGame(): void {
        if (this.player.alive) {
            this.moveEnemies();
            this.enemyShoot();
            this.moveProjectiles();
            this.checkCollisions();

            if (this.enemies.length === 0) {
                this.spawnEnemyGroup();
                if (this.enemySpeed < 3) {
                    this.enemySpeed += this.speedIncrement;
                }
            }

            this.requestUpdate();
            requestAnimationFrame(() => this.updateGame());
        } else {
            // Handle game over logic here
            console.log("Game Over");
        }
    }


    private moveEnemies(): void {
        const leftMost: number = Math.min(...this.enemies.map(enemy => enemy.position.x));
        const rightMost: number = Math.max(...this.enemies.map(enemy => enemy.position.x + enemy.size.width));

        // Check if the group has reached either edge of the game board
        if (this.enemyDirection === "right" && rightMost > this.offsetWidth - 10 ||
            this.enemyDirection === "left" && leftMost < 10) {
            // Time to move down and reverse direction
            this.enemyDropDown = true;
        }

        if (this.enemyDropDown) {
            // Move the entire group down and flip direction
            this.enemies = this.enemies.map(enemy => ({
                ...enemy,
                position: {
                    x: enemy.position.x,
                    y: enemy.position.y + (this.moveDownStep * (this.difficulty / 2))
                }
            }));
            this.enemyDirection = this.enemyDirection === "right" ? "left" : "right";
            this.enemyDropDown = false; // Reset for the next horizontal movement
        } else {
            // Move the group horizontally
            const moveStep: number = this.enemyDirection === "right" ? 3 : -1 * 3;
            this.enemies = this.enemies.map(enemy => ({
                ...enemy,
                position: {x: enemy.position.x + moveStep, y: enemy.position.y}
            }));
        }
    }

    private moveProjectiles(): void {
        this.projectiles = this.projectiles.map(projectile => ({
            ...projectile,
            positionY: projectile.direction === "up" ? projectile.positionY - projectile.speed : projectile.positionY + projectile.speed
        })).filter(projectile => (projectile.direction === "up" && projectile.positionY > 0) ||
            (projectile.direction === "down" && projectile.positionY < this.offsetHeight));
    }


    private checkCollisions(): void {
        const projectilesToRemove: Set<number> = new Set();
        const enemiesToRemove: Set<number> = new Set();

        // Refactored to use traditional for loop for early exit capability
        for (let pIndex: number = 0; pIndex < this.projectiles.length; pIndex++) {
            const projectile: {
                positionX: number,
                positionY: number,
                direction: "up" | "down",
                shooter: "player" | "enemy",
                speed: number
            } = this.projectiles[pIndex];
            if (projectile.shooter === "player") {
                for (let eIndex: number = 0; eIndex < this.enemies.length; eIndex++) {
                    const enemy: any = this.enemies[eIndex];
                    const projectileRect: DOMRect = new DOMRect(projectile.positionX, projectile.positionY, 2, 10);
                    const enemyRect: DOMRect = new DOMRect(enemy.position.x, enemy.position.y, enemy.size.width, enemy.size.height);

                    if (intersects(projectileRect, enemyRect)) {
                        projectilesToRemove.add(pIndex);
                        enemy.health -= 1;

                        if (enemy.health <= 0) {
                            enemiesToRemove.add(eIndex);
                            this.score += enemy.scoreValue;
                        }
                        break;
                    }
                }
            }
        }

        const playerParent: HTMLElement = this.shadowRoot?.querySelector("player-wizard") as HTMLElement;

        const playerElement: HTMLElement = playerParent.shadowRoot?.firstElementChild as HTMLElement;

        if (playerElement) {
            const playerRect: DOMRect = playerElement.getBoundingClientRect();
            // Convert playerRect from global to local coordinates if necessary
            const gameBoardRect: DOMRect = this.getBoundingClientRect();
            const localPlayerRect: DOMRect = new DOMRect(
                playerRect.left - gameBoardRect.left,
                playerRect.top - gameBoardRect.top,
                playerRect.width,
                playerRect.height
            );

            this.projectiles.forEach((projectile, index) => {
                if (projectile.shooter === "enemy") {
                    const projectileRect: DOMRect = new DOMRect(projectile.positionX, projectile.positionY, 2, 10);
                    if (intersects(projectileRect, localPlayerRect)) {
                        projectilesToRemove.add(index);
                        this.player.alive = false;

                    }
                }
            });

            this.enemies.forEach((enemy, index) => {
                const enemyRect: DOMRect = new DOMRect(enemy.position.x, enemy.position.y, enemy.size.width, enemy.size.height);
                if (intersects(enemyRect, localPlayerRect)) {
                    enemiesToRemove.add(index);
                    this.player.alive = false;
                }
            });
        }

        this.projectiles = this.projectiles.filter((_, index) => !projectilesToRemove.has(index));
        this.enemies = this.enemies.filter((_, index) => !enemiesToRemove.has(index));
        this.requestUpdate();
    }

    private startNewGame(): void {
        this.gameStarted = true;
        this.spawnEnemyGroup();
        const difficulty: difficultyEnum = getDifficultyFromSession() as difficultyEnum;
        const index: number | undefined = this.getIndexByDifficulty(difficulty);
        this.difficulty = index ?? 1;
        requestAnimationFrame(() => this.updateGame());
    }

    private getIndexByDifficulty(difficulty: string): number | undefined {
        const difficultyEnumValue: difficultyEnum = difficultyEnum[difficulty as keyof typeof difficultyEnum];
        if (difficultyEnumValue) {
            return difficultyIndex[difficultyEnumValue];
        }
        return undefined; // Return undefined if the difficulty does not exist
    }


    private retryGame(): void {
        this.gameStarted = true;
        this.player.alive = true;
        this.score = 0;
        this.enemies = [];
        this.projectiles = [];
        this.startGame(); // Start a new game immediately
    }

    private async leaveGame(): Promise<void> {
        await postScore(this.score);
        localStorage.removeItem("start-dragon-invaders");
        location.reload();
    }

    protected render(): TemplateResult {
        if (!this.gameStarted) {
            return html`
                <div class="start-screen">
                    <h1>Dragon Invaders</h1>
                    <p>Controls:</p>
                    <p>Move Left: Arrow Left<br>Move Right: Arrow Right<br>Shoot: Spacebar</p>
                    <button
                        @click="${this.startNewGame}"
                        @keydown=${(e: KeyboardEvent): void => {
                            if (e.key === "Enter") this.startNewGame();
                        }}
                    >Start Game
                    </button>
                </div>
            `;
        } else if (!this.player.alive) {
            return html`
                <div class="game-over-screen">
                    <h1>Game Over</h1>
                    <p>Your score: ${this.score}</p>
                    <button
                        @click="${this.retryGame}"
                        @keydown=${(e: KeyboardEvent): void => {
                            if (e.key === "Enter") this.retryGame();
                        }}
                    >Retry
                    </button>
                    <button
                        @click="${this.leaveGame}"
                        @keydown=${async (e: KeyboardEvent): Promise<void> => {
                            if (e.key === "Enter") await this.leaveGame();
                        }}
                    >Return
                    </button>
                </div>
            `;
        } else {
            // Existing game rendering logic here
            return html`
                <player-wizard></player-wizard>
                ${this.enemies.map(enemy => html`
                    <enemy-dragon .position=${enemy.position} .imageUrl=${enemy.imageUrl}></enemy-dragon>
                `)}
                ${this.projectiles.map(projectile => html`
                    <game-projectile .positionX=${projectile.positionX} .positionY=${projectile.positionY}
                                     .direction=${projectile.direction}
                                     shooter=${projectile.shooter}></game-projectile>`)}
                <div class="score">Score: ${this.score}</div>
            `;
        }
    }
}
